import Dexie from "dexie";
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { downloadBlob, readFileAsText } from "../../lib/utils";
import { exportDBToJSON, importDBFromJSON, RestoreOptions } from "../../lib/db";
import { I18n, SupportedLocale, Translations } from "../../lib/i18n";

// ── Context value ─────────────────────────────────────
interface StorageContextValue {
  db: Dexie | undefined;
  i18n: I18n;
  locale: SupportedLocale;
  setLocale: (locale: SupportedLocale) => void;
  exportBackup: () => Promise<void>;
  importBackup: (file: File, options?: RestoreOptions) => Promise<{
    success: boolean;
    tablesRestored: string[];
    errors: string[];
  }>;
  printPage: () => void;
}

const StorageContext = createContext<StorageContextValue | null>(null);

interface StorageProviderProps {
  children: React.ReactNode;
  db?: Dexie | undefined;
  initialLocale?: SupportedLocale;
  translations?: Translations;
  appName?: string;
}

export function StorageProvider({
  children,
  db = undefined,
  initialLocale = "en",
  translations = {},
  appName = "app",
}: StorageProviderProps) {
  const [locale, setLocaleState] = useState<SupportedLocale>(initialLocale);

  const i18n = useMemo(() => {
    const instance = new I18n(locale, translations);
    return instance;
  }, [locale, translations]);

  const setLocale = useCallback((l: SupportedLocale) => {
    setLocaleState(l);
    i18n.setLocale(l);
  }, [i18n]);

  const exportBackup = useCallback(async () => {
    if (db === undefined) {
      console.warn("StorageProvider: no db provided");
      return;
    }
    const json = await exportDBToJSON(db);
    const blob = new Blob([json], { type: "application/json" });
    const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
    downloadBlob(blob, `${appName}-backup-${ts}.json`);
  }, [db, appName]);

  const importBackup = useCallback(
    async (file: File, options?: RestoreOptions) => {
      if (db === undefined) return { success: false, tablesRestored: [], errors: ["No DB"] };
      const text = await readFileAsText(file);
      return importDBFromJSON(db, text, options);
    },
    [db]
  );

  const printPage = useCallback(() => {
    window.print();
  }, []);

  const value = useMemo<StorageContextValue>(
    () => ({ db, i18n, locale, setLocale, exportBackup, importBackup, printPage }),
    [db, i18n, locale, setLocale, exportBackup, importBackup, printPage]
  );

  return (
    <StorageContext.Provider value={value}>{children}</StorageContext.Provider>
  );
}

export function useStorage(): StorageContextValue {
  const ctx = useContext(StorageContext);
  if (!ctx) throw new Error("useStorage must be used inside <StorageProvider>");
  return ctx;
}
